import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FileQuestion, AlertCircle, CheckCircle, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api, { formatApiErrorDetail } from '../lib/api';
import { queryTypes } from '../mock/mockData';

export default function SubmitQuery() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    email: '',
    queryType: '',
    description: '',
    priority: 'Standard'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    setFormData((prev) => ({
      ...prev,
      name: user.fullName || '',
      email: user.email || '',
      companyName: user.companyName || ''
    }));

    const planType = location.state?.planType;
    if (planType === 'priority') {
      setFormData(prev => ({ ...prev, priority: 'Priority' }));
    }
  }, [user, location.state]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSelectChange = (value) => {
    setFormData({
      ...formData,
      queryType: value
    });
    setError('');
  };

  const handlePriorityChange = (value) => {
    setFormData({
      ...formData,
      priority: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.email || !formData.queryType || !formData.description) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await api.post('/queries', formData);
      await refreshUser();
      navigate('/dashboard', {
        state: {
          message: 'Query submitted successfully! You will receive a response within the SLA timeframe.'
        }
      });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  const isFreeQuery = !user.freeQueryUsed;
  const estimatedCost = isFreeQuery ? 0 : (formData.priority === 'Priority' ? 200 : 100);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="bg-[#0078D4] p-3 rounded-full">
              <FileQuestion className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[#003366]">Submit New Query</h1>
              <p className="text-gray-600">Get expert Microsoft 365 assistance</p>
            </div>
          </div>
        </div>

        {/* Free Query Alert */}
        {isFreeQuery && (
          <Alert className="mb-6 bg-green-50 border-green-200">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <AlertDescription className="text-green-800">
              <strong>This is your free query!</strong> Submit your first query at no cost with our standard 24-hour SLA.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <Card className="shadow-lg">
              <CardHeader>
                <h2 className="text-xl font-bold text-[#003366]">Query Details</h2>
                <p className="text-sm text-gray-600">
                  Provide detailed information to help us understand and resolve your issue
                </p>
              </CardHeader>

              <CardContent>
                {error && (
                  <Alert variant="destructive" className="mb-6">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-gray-700 font-medium">
                        Your Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleChange}
                        className="mt-1 h-11"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="companyName" className="text-gray-700 font-medium">
                        Company Name
                      </Label>
                      <Input
                        id="companyName"
                        name="companyName"
                        type="text"
                        placeholder="Your Company Inc."
                        value={formData.companyName}
                        onChange={handleChange}
                        className="mt-1 h-11"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-medium">
                      Email Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="mt-1 h-11"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="queryType" className="text-gray-700 font-medium">
                      Query Type <span className="text-red-500">*</span>
                    </Label>
                    <Select value={formData.queryType} onValueChange={handleSelectChange}>
                      <SelectTrigger className="mt-1 h-11">
                        <SelectValue placeholder="Select query type" />
                      </SelectTrigger>
                      <SelectContent>
                        {queryTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="description" className="text-gray-700 font-medium">
                      Problem Statement / Requirement <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="description"
                      name="description"
                      placeholder="Please describe your issue or requirement in detail. Include any error messages, steps you've already taken, and your expected outcome..."
                      value={formData.description}
                      onChange={handleChange}
                      className="mt-1 min-h-[200px]"
                      required
                    />
                    <p className="text-sm text-gray-500 mt-2">
                      The more details you provide, the better we can assist you.
                    </p>
                  </div>

                  <div>
                    <Label className="text-gray-700 font-medium mb-3 block">
                      Priority Level
                    </Label>
                    <RadioGroup 
                      value={formData.priority} 
                      onValueChange={handlePriorityChange}
                      disabled={isFreeQuery}
                    >
                      <div className="flex items-center space-x-3 p-4 border rounded-lg">
                        <RadioGroupItem value="Standard" id="standard" />
                        <Label htmlFor="standard" className="flex-1 cursor-pointer">
                          <div className="font-semibold text-gray-900">Standard (24-hour SLA)</div>
                          <div className="text-sm text-gray-600">
                            {isFreeQuery ? 'Free for your first query' : '$100'}
                          </div>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-3 p-4 border rounded-lg">
                        <RadioGroupItem value="Priority" id="priority" disabled={isFreeQuery} />
                        <Label htmlFor="priority" className={`flex-1 ${isFreeQuery ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                          <div className="font-semibold text-gray-900">Priority (1-hour SLA)</div>
                          <div className="text-sm text-gray-600">
                            {isFreeQuery ? 'Not available for free query' : '$200 - Urgent matters'}
                          </div>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-[#0078D4] hover:bg-[#005a9e] font-semibold text-lg"
                  >
                    {loading ? 'Submitting...' : 'Submit Query'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <Card className="shadow-lg sticky top-24">
              <CardHeader>
                <h3 className="text-lg font-bold text-[#003366]">Query Summary</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Priority</p>
                  <p className="font-semibold text-gray-900">{formData.priority}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Response SLA</p>
                  <p className="font-semibold text-gray-900">
                    {formData.priority === 'Priority' ? '1 hour' : '24 hours'}
                  </p>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600 mb-1">Estimated Cost</p>
                  <p className="text-3xl font-bold text-[#0078D4]">
                    ${estimatedCost}
                  </p>
                  {isFreeQuery && (
                    <p className="text-sm text-green-600 font-medium mt-1">
                      First query is FREE!
                    </p>
                  )}
                </div>

                <Alert className="bg-blue-50 border-blue-200">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-sm text-blue-900">
                    You'll receive a response via email or Teams meeting within the SLA timeframe.
                  </AlertDescription>
                </Alert>

                <div className="text-xs text-gray-500 space-y-2">
                  <p>✓ Detailed solution documentation</p>
                  <p>✓ Follow-up assistance included</p>
                  <p>✓ Teams meeting available if needed</p>
                  <p>✓ 100% satisfaction guarantee</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
